import { useCallback, useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { Stack, StackItem } from '@astryxdesign/core/Stack'
import { Center } from '@astryxdesign/core/Center'
import { Heading } from '@astryxdesign/core/Heading'
import { Text } from '@astryxdesign/core/Text'
import { Button } from '@astryxdesign/core/Button'
import { Icon } from '@astryxdesign/core/Icon'
import { Banner } from '@astryxdesign/core/Banner'
import { Spinner } from '@astryxdesign/core/Spinner'
import { StatusDot } from '@astryxdesign/core/StatusDot'
import { Token } from '@astryxdesign/core/Token'
import { Switch } from '@astryxdesign/core/Switch'
import { Selector } from '@astryxdesign/core/Selector'
import { VisuallyHidden } from '@astryxdesign/core/VisuallyHidden'
import { Table, TableHeader, TableBody, TableRow, TableCell, TableHeaderCell } from '@astryxdesign/core/Table'
import { readOpeningHours, updateOpeningHours } from './consoleApi'
import {
    WEEKDAYS,
    conflictLook,
    conflictsTitle,
    courtTitle,
    dayHoursError,
    dayShort,
    dayTitle,
    failureText,
    hoursAreValid,
    hoursOptions,
    normalizeConflicts,
    normalizeHours,
    sameHours,
    slotTime,
    slotsPreviewText,
    todayParts,
} from './consoleText'

// The opening hours: one row per weekday, Monday first, each open or closed,
// with its opening and closing time and the slots they give the players.
//
// Nothing is saved until "Enregistrer". The server refuses hours that some
// future booking would no longer fit in, and says which bookings: they are
// listed above the form, each with a way to its day, and the form stays as
// it was edited so the admin can change it and try again.
const SELECTOR_WIDTH = 128

// Fixed widths for every column but the preview, which takes the rest; a
// table cell gives way to its neighbours unless it is told not to.
const fixed = width => ({ width, minWidth: width })
const dayColumnStyle = fixed('calc(var(--spacing-10) * 3)')
const switchColumnStyle = fixed('calc(var(--spacing-10) * 3)')
const timeColumnStyle = fixed('calc(var(--spacing-10) * 4)')
const conflictColumns = {
    date: fixed('calc(var(--spacing-10) * 5)'),
    time: fixed('calc(var(--spacing-10) * 2)'),
    court: fixed('calc(var(--spacing-10) * 4)'),
    action: fixed('calc(var(--spacing-10) * 4)'),
}

const OPTIONS = hoursOptions()

export default function CourtHours({ clubId, onOpenDay, onSessionLost }) {
    const [load, setLoad] = useState({ status: 'loading' })
    const [days, setDays] = useState([])
    const [notice, setNotice] = useState(null) // { status, title } or { conflicts }
    const [isSaving, setSaving] = useState(false)

    const reload = useCallback(async () => {
        setLoad({ status: 'loading' })
        let answer
        try {
            answer = await readOpeningHours(clubId)
        } catch (e) {
            answer = { status: 0 }
        }
        if (answer.status === 200 && answer.json) {
            const hours = normalizeHours(answer.json.hours)
            setLoad({ status: 'ready', hours })
            setDays(hours.days)
        } else if (answer.status === 401) {
            onSessionLost()
        } else {
            setLoad({ status: 'error', title: failureText(answer.status) })
        }
    }, [clubId, onSessionLost])

    useEffect(() => {
        reload()
    }, [reload])

    const hours = load.status === 'ready' ? load.hours : null
    const isChanged = !!hours && !sameHours(days, hours.days)
    const isValid = useMemo(() => hoursAreValid(days), [days])

    const change = (index, patch) => {
        setDays(current => current.map((day, i) => (i === index ? { ...day, ...patch } : day)))
    }

    const save = async () => {
        setSaving(true)
        setNotice(null)
        let answer
        try {
            answer = await updateOpeningHours(clubId, days)
        } catch (e) {
            answer = { status: 0 }
        }
        setSaving(false)
        if (answer.status === 200 && answer.json) {
            const after = normalizeHours(answer.json.hours)
            if (answer.json.saved) {
                setLoad({ status: 'ready', hours: after })
                setDays(after.days)
                setNotice({ status: 'success', title: "Horaires enregistrés. Ils s'appliquent dès aujourd'hui aux réservations des joueurs." })
            } else {
                // Refused: the club's hours are what the server says they
                // still are, and the form keeps what the admin typed.
                setLoad({ status: 'ready', hours: after })
                setNotice({ conflicts: normalizeConflicts(answer.json) })
            }
        } else if (answer.status === 401) {
            onSessionLost()
        } else {
            setNotice({ status: 'error', title: failureText(answer.status) })
        }
    }

    const header = (
        <Stack gap={1}>
            <Heading level={2}>Horaires d'ouverture</Heading>
            <Text color="secondary">Les créneaux durent 1 h 30. Le premier commence à l'ouverture, le dernier se termine au plus tard à la fermeture.</Text>
        </Stack>
    )

    if (load.status === 'loading') {
        return (
            <Stack gap={4}>
                {header}
                <Center axis="both" padding={10}>
                    <Spinner label="Chargement des horaires" />
                </Center>
            </Stack>
        )
    }
    if (load.status === 'error') {
        return (
            <Stack gap={4}>
                {header}
                <Stack gap={3} hAlign="start">
                    <Banner status="error" title={load.title} />
                    <Button label="Réessayer" variant="secondary" onClick={reload} />
                </Stack>
            </Stack>
        )
    }

    let banner = null
    if (notice && notice.conflicts) {
        banner = <ConflictsBanner conflicts={notice.conflicts} onOpenDay={onOpenDay} onDismiss={() => setNotice(null)} />
    } else if (notice) {
        banner = (
            <Banner
                status={notice.status}
                title={notice.title}
                isDismissable
                dismissLabel="Masquer le message"
                onDismiss={() => setNotice(null)}
            />
        )
    }

    return (
        <Stack gap={4}>
            {header}
            {banner}
            <Table density="compact" dividers="rows">
                <TableHeader>
                    <TableRow>
                        <TableHeaderCell style={dayColumnStyle}>Jour</TableHeaderCell>
                        <TableHeaderCell style={switchColumnStyle}>Statut</TableHeaderCell>
                        <TableHeaderCell style={timeColumnStyle}>Ouverture</TableHeaderCell>
                        <TableHeaderCell style={timeColumnStyle}>Fermeture</TableHeaderCell>
                        <TableHeaderCell>Créneaux</TableHeaderCell>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {days.map((day, index) => (
                        <DayRow
                            key={WEEKDAYS[index]}
                            name={capitalize(WEEKDAYS[index])}
                            day={day}
                            onChange={patch => change(index, patch)}
                        />
                    ))}
                </TableBody>
            </Table>
            {hours.is_default && (
                <Banner
                    status="info"
                    title="Ce club n'a pas encore d'horaires : ce sont ceux par défaut, 8:00 – 23:00 tous les jours."
                />
            )}
            <Stack direction="horizontal" gap={3} vAlign="center" wrap="wrap">
                <Button
                    label="Enregistrer"
                    variant="primary"
                    isDisabled={!isChanged || !isValid}
                    isLoading={isSaving}
                    onClick={save}
                />
                <Button
                    label="Annuler les modifications"
                    variant="secondary"
                    isDisabled={!isChanged || isSaving}
                    onClick={() => setDays(hours.days)}
                />
                {!isValid && <Text type="supporting">Corrigez les jours signalés pour enregistrer.</Text>}
            </Stack>
        </Stack>
    )
}

CourtHours.propTypes = {
    clubId: PropTypes.string.isRequired,
    // Opens the day view on { year, month, day }.
    onOpenDay: PropTypes.func.isRequired,
    // Called when a console call answers 401: the session is gone.
    onSessionLost: PropTypes.func.isRequired,
}

function capitalize(text) {
    return text.charAt(0).toUpperCase() + text.slice(1)
}

// One weekday. A closed day keeps the times it had, greyed out, so opening
// it again brings them back.
function DayRow({ name, day, onChange }) {
    const error = dayHoursError(day)
    const status = error ? { type: 'error', message: error } : undefined

    let preview
    if (error) {
        preview = (
            <Stack direction="horizontal" gap={1.5} vAlign="center">
                <Icon icon="error" color="error" size="sm" />
                <Text size="sm">{error}</Text>
            </Stack>
        )
    } else {
        preview = (
            <Text size="sm" color={day.closed ? 'secondary' : 'primary'} hasTabularNumbers>
                {slotsPreviewText(day)}
            </Text>
        )
    }

    return (
        <TableRow>
            <TableCell style={dayColumnStyle}>
                <Text weight="medium">{name}</Text>
            </TableCell>
            <TableCell style={switchColumnStyle}>
                <Switch
                    label={day.closed ? 'Fermé' : 'Ouvert'}
                    value={!day.closed}
                    onChange={isOpen => onChange({ closed: !isOpen })}
                />
            </TableCell>
            <TableCell style={timeColumnStyle}>
                <Selector
                    label={`${name} : ouvre à`}
                    isLabelHidden
                    size="sm"
                    width={SELECTOR_WIDTH}
                    options={OPTIONS}
                    value={String(day.open_minute)}
                    isDisabled={day.closed}
                    onChange={value => onChange({ open_minute: Number(value) })}
                />
            </TableCell>
            <TableCell style={timeColumnStyle}>
                <Selector
                    label={`${name} : ferme à`}
                    isLabelHidden
                    size="sm"
                    width={SELECTOR_WIDTH}
                    options={OPTIONS}
                    value={String(day.close_minute)}
                    isDisabled={day.closed}
                    status={status}
                    statusVariant="tooltip"
                    onChange={value => onChange({ close_minute: Number(value) })}
                />
            </TableCell>
            <TableCell>{preview}</TableCell>
        </TableRow>
    )
}

DayRow.propTypes = {
    name: PropTypes.string.isRequired,
    day: PropTypes.shape({
        closed: PropTypes.bool.isRequired,
        open_minute: PropTypes.number.isRequired,
        close_minute: PropTypes.number.isRequired,
    }).isRequired,
    onChange: PropTypes.func.isRequired,
}

// The bookings that stopped a save, in date and time order, each with a
// way to the day it is on - where it can be cancelled or unblocked. The
// list sits under the warning rather than inside it: a banner's body is
// sized for a few lines of text, not for rows. The space under it keeps the
// form's own table, which reaches up into whatever is above it, apart.
function ConflictsBanner({ conflicts, onOpenDay, onDismiss }) {
    const thisYear = todayParts().year
    return (
        <Stack gap={2} paddingBlockEnd={6}>
            <Banner
                status="warning"
                title={conflictsTitle(conflicts.length)}
                description="Annulez ces parties ou débloquez ces créneaux, ou choisissez d'autres horaires. Rien n'a été enregistré."
                isDismissable
                dismissLabel="Masquer le message"
                onDismiss={onDismiss}
            />
            <Table density="compact" dividers="rows" verticalAlign="middle">
                <TableHeader>
                    <TableRow>
                        <TableHeaderCell style={conflictColumns.date}>Date</TableHeaderCell>
                        <TableHeaderCell style={conflictColumns.time}>Heure</TableHeaderCell>
                        <TableHeaderCell style={conflictColumns.court}>Terrain</TableHeaderCell>
                        <TableHeaderCell>Réservation</TableHeaderCell>
                        <TableHeaderCell style={conflictColumns.action}><VisuallyHidden>Action</VisuallyHidden></TableHeaderCell>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {conflicts.map(conflict => {
                        const date = { year: conflict.year, month: conflict.month, day: conflict.day }
                        return (
                            <TableRow key={conflict.booking_id || `${conflict.court_id}-${conflict.year}-${conflict.month}-${conflict.day}-${conflict.start_minute}`}>
                                <TableCell style={conflictColumns.date}>
                                    <Text>{conflict.year === thisYear ? dayShort(date) : dayTitle(date)}</Text>
                                </TableCell>
                                <TableCell style={conflictColumns.time}>
                                    <Text hasTabularNumbers>{slotTime(conflict.start_minute)}</Text>
                                </TableCell>
                                <TableCell style={conflictColumns.court}>
                                    <Text>{courtTitle(conflict)}</Text>
                                </TableCell>
                                <TableCell>
                                    <ConflictWhat conflict={conflict} />
                                </TableCell>
                                <TableCell style={conflictColumns.action}>
                                    <Stack direction="horizontal" hAlign="end">
                                        <Button label="Ouvrir le jour" variant="secondary" size="sm" onClick={() => onOpenDay(date)} />
                                    </Stack>
                                </TableCell>
                            </TableRow>
                        )
                    })}
                </TableBody>
            </Table>
        </Stack>
    )
}

ConflictsBanner.propTypes = {
    conflicts: PropTypes.arrayOf(PropTypes.object).isRequired,
    onOpenDay: PropTypes.func.isRequired,
    onDismiss: PropTypes.func.isRequired,
}

function ConflictWhat({ conflict }) {
    const look = conflictLook(conflict)
    const lead = look.token
        ? <Token label={look.text} color={look.token} size="sm" />
        : (
            <Stack direction="horizontal" gap={1.5} vAlign="center">
                <StatusDot variant={look.dot} label={look.detail || look.text} />
                <Text>{look.text}</Text>
            </Stack>
        )
    return (
        <Stack direction="horizontal" gap={2} vAlign="center">
            <StackItem size="static">{lead}</StackItem>
            {look.detail && <Text color="secondary">{look.detail}</Text>}
        </Stack>
    )
}

ConflictWhat.propTypes = {
    conflict: PropTypes.object.isRequired,
}
